import type { AgentPendingAction, AgentRunResponse } from '../api/agents'
import type { AgentActivityKind, AgentActivityState } from '../components/agent/AgentActivityBar'
import { AGENT_TOOL_LABELS, agentToolLabel } from '../components/agent/agentDisplay'
import type { AgentTimelineBlock } from '../components/agent/AgentRunCard'
import type { AgentProcessLine } from '../components/agent/AgentProcessFold'
import type { AgentTodoItem } from '../components/agent/AgentTodoFold'

export type ApplyAgentStreamEventResult = {
  timeline: AgentTimelineBlock[]
  pendingAction?: AgentPendingAction | null
  status?: string
  showConfirm?: boolean
  runPublicId?: string | null
  activity?: AgentActivityState | null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

function mapTodoStatus(status: unknown): AgentTodoItem['status'] {
  switch (asString(status)) {
    case 'completed':
    case 'done':
      return 'completed'
    case 'cancelled':
    case 'canceled':
      return 'cancelled'
    case 'in_progress':
    case 'running':
    case 'awaiting_confirmation':
    case 'awaiting':
      return 'in_progress'
    default:
      return 'pending'
  }
}

const PROCESS_KINDS = new Set<AgentProcessLine['kind']>([
  'thought',
  'tool',
  'observation',
  'plan',
  'mode',
  'read',
  'note',
  'question',
  'subagent',
])

const ACTIVITY_KINDS = new Set<AgentActivityKind>([
  'thinking',
  'planning',
  'tool',
  'subagent',
  'verifying',
  'waiting',
  'confirm',
  'done',
])

function mapProcessKind(kind: unknown): AgentProcessLine['kind'] {
  const value = asString(kind, 'note')
  return PROCESS_KINDS.has(value as AgentProcessLine['kind'])
    ? (value as AgentProcessLine['kind'])
    : 'note'
}

function mapActivityKind(kind: unknown): AgentActivityKind | undefined {
  const value = asString(kind)
  return ACTIVITY_KINDS.has(value as AgentActivityKind)
    ? (value as AgentActivityKind)
    : undefined
}

function mapProcessLines(raw: unknown): AgentProcessLine[] {
  if (!Array.isArray(raw)) return []
  return raw
    .filter(isRecord)
    .map((line, index) => {
      let label = asString(line.label, '步骤')
      // 后端把工具名放在 meta.toolName（token 等内部参数不下发）：渲染为「工具调用 · 中文名」
      let meta: string | undefined
      if (isRecord(line.meta) && typeof line.meta.toolName === 'string' && line.meta.toolName.trim()) {
        label = `${label} · ${agentToolLabel(line.meta.toolName.trim())}`
      } else if (
        // 兜底：历史数据把工具内部名直接当 label 下发（如已取消行），翻译为中文
        typeof line.label === 'string' &&
        AGENT_TOOL_LABELS[line.label.trim()]
      ) {
        label = `工具调用 · ${agentToolLabel(line.label.trim())}`
      } else if (typeof line.meta === 'string') {
        meta = line.meta
      } else if (line.meta != null) {
        meta = JSON.stringify(line.meta)
      }
      return {
        id: asString(line.id, `line-${index}`),
        kind: mapProcessKind(line.kind),
        label,
        status: typeof line.status === 'string' ? line.status : undefined,
        meta,
        detail: typeof line.detail === 'string' ? line.detail : undefined,
      }
    })
}

function upsertBlock(
  timeline: AgentTimelineBlock[],
  block: AgentTimelineBlock,
): AgentTimelineBlock[] {
  const index = timeline.findIndex((item) => item.id === block.id)
  if (index < 0) return [...timeline, block]
  const next = timeline.slice()
  next[index] = block
  return next
}

/** 思考块收尾时把用时烙进标题：「第 n 轮思考完成（用时 x 秒）」 */
function withElapsedSeconds(summary: string, startedAt: number | undefined): string {
  if (startedAt == null || summary.includes('用时')) return summary
  const seconds = Math.max(1, Math.round((Date.now() - startedAt) / 1000))
  return `${summary}（用时 ${seconds} 秒）`
}

/**
 * 收尾仍在 running 的思考块。后端要等整轮流结束才发 reasoning done，
 * 而正文 token 在流内是紧跟思维链之后下发的——若只靠 done 事件，
 * 正文流式期间思考块会一直卡在「思考中」。因此正文开始输出 / 运行到达终态时，
 * 一律把还没收尾的思考块就地置为「思考完成」。
 */
function closeRunningReasoning(
  blocks: AgentTimelineBlock[],
  exceptLineId?: string,
): AgentTimelineBlock[] {
  return blocks.map((item) =>
    item.type === 'process' &&
    item.mergeKey === 'reasoning' &&
    item.status === 'running' &&
    (exceptLineId == null || !item.lines.some((line) => line.id === exceptLineId))
      ? {
          ...item,
          status: 'done',
          forceOpen: false,
          summary: withElapsedSeconds(item.summary.replace('思考中', '思考完成'), item.startedAt),
        }
      : item,
  )
}

/** 时间线生图确认块的参数行：模型 / 画幅 / 张数 / 参考图 / 提示词（有值才显示） */
function generationParamLines(
  input: Record<string, unknown> | undefined,
  token: string,
): AgentProcessLine[] {
  if (!isRecord(input)) return []
  const lines: AgentProcessLine[] = []
  const push = (key: string, label: string, detail: string) => {
    if (!detail.trim()) return
    lines.push({ id: `confirm-${token}-param-${key}`, kind: 'note', label, detail, status: 'awaiting_confirmation' })
  }
  const aspect = asString(input.aspectRatio)
  const size = asString(input.size)
  const n = Number(input.n)
  const refs = Array.isArray(input.referenceImages) ? input.referenceImages.length : 0
  push('model', '模型', asString(input.model))
  push('aspect', '画幅', aspect || size)
  if (Number.isFinite(n) && n > 0) push('n', '张数', `${n} 张`)
  if (refs > 0) push('refs', '参考图', `${refs} 张`)
  push('prompt', '提示词', asString(input.prompt))
  return lines
}

function parsePendingAction(payload: unknown): AgentPendingAction | null {  if (!isRecord(payload)) return null
  const token = asString(payload.token)
  const toolName = asString(payload.toolName)
  if (!token || !toolName) return null
  return {
    token,
    runPublicId: typeof payload.runPublicId === 'string' ? payload.runPublicId : null,
    toolName,
    input: isRecord(payload.input) ? payload.input : {},
    assistantThought: asString(payload.assistantThought),
  }
}

function parseFinalResponse(payload: unknown): Partial<AgentRunResponse> | null {
  if (!isRecord(payload)) return null
  return payload as Partial<AgentRunResponse>
}

/** 时间线图片块中的单张图片 */
export type AgentTimelineImage = {
  src: string
  alt?: string
  /** 后端 objectKey；有值时图片加载失败可走 refresh-url 自动补签（七牛签名链 30 分钟过期） */
  objectKey?: string
}

/** 仅模型内部检索使用的图库工具：结果不渲染图片条（展示走 display_images） */
const GALLERY_TOOLS = new Set(['timeline.gallery.list', 'reference.gallery.list'])

/**
 * 从工具结果中提取可展示的图片（白名单制：只认已知工具的输出结构，不做启发式猜 URL）。
 * - display_images：模型显式挑选、要展示给用户看的图片
 * - timeline.gallery.list / reference.gallery.list：模型内部检索用的图库工具，
 *   恒不渲染——模型读图库（查有没有某张图、取上下文）时不应把整页图片倒进对话，
 *   要展示时模型会通过 display_images 输出选中的那几张
 */
function extractImagesFromToolOutput(
  toolName: string,
  output: unknown,
): AgentTimelineImage[] {
  if (!isRecord(output)) return []
  if (GALLERY_TOOLS.has(toolName)) return []
  const images: AgentTimelineImage[] = []
  const pushImage = (src: unknown, alt?: unknown, objectKey?: unknown) => {
    if (typeof src !== 'string') return
    const url = src.trim()
    // 只认 http/https 与站内 /uploads 直链：图库历史数据里混有「存失败：…」类文本 url，
    // 原样进 <img> 必然裂图，在此边界过滤
    if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('/uploads/')) return
    images.push({
      src: url,
      alt: typeof alt === 'string' && alt.trim() ? alt.trim() : undefined,
      objectKey: typeof objectKey === 'string' && objectKey.trim() ? objectKey.trim() : undefined,
    })
  }
  if (toolName === 'display_images') {
    if (Array.isArray(output.items)) {
      for (const item of output.items) {
        if (isRecord(item)) pushImage(item.url, item.title)
      }
    }
    return images
  }
  if (toolName === 'timeline.gallery.list') {
    if (Array.isArray(output.rounds)) {
      for (const round of output.rounds) {
        if (!isRecord(round) || !Array.isArray(round.items)) continue
        const prompt = typeof round.prompt === 'string' ? round.prompt : undefined
        for (const item of round.items) {
          if (isRecord(item)) pushImage(item.src, prompt, item.objectKey)
        }
      }
    }
    return images
  }
  if (toolName === 'reference.gallery.list') {
    if (Array.isArray(output.items)) {
      for (const item of output.items) {
        if (isRecord(item)) pushImage(item.url, item.prompt ?? item.name, item.objectKey)
      }
    }
    return images
  }
  return []
}

/** 追加/合并图片块：同 id 合并去重（分页重复引用同一批图时不重复渲染） */
function appendImagesBlock(
  timeline: AgentTimelineBlock[],
  id: string,
  toolName: string,
  images: AgentTimelineImage[],
): AgentTimelineBlock[] {
  const existing = timeline.find((item) => item.id === id)
  if (existing && existing.type === 'images') {
    const seen = new Set(existing.images.map((item) => item.src))
    const merged = [...existing.images, ...images.filter((item) => !seen.has(item.src))]
    return timeline.map((item) =>
      item.id === id ? { ...existing, images: merged } : item,
    )
  }
  return [...timeline, { id, type: 'images', toolName, images }]
}

function parseActivity(payload: unknown): AgentActivityState | null {
  if (!isRecord(payload)) return null
  const label = asString(payload.label || payload.text || payload.message)
  if (!label) return null
  return {
    label,
    kind: mapActivityKind(payload.kind),
  }
}

/** 将 SSE 事件应用到时间线块列表 */
export function applyAgentStreamEvent(
  timeline: AgentTimelineBlock[],
  eventName: string,
  payload: unknown,
): ApplyAgentStreamEventResult {
  switch (eventName) {
    case 'activity': {
      return {
        timeline,
        activity: parseActivity(payload),
        status: 'running',
      }
    }

    case 'todos': {
      if (!isRecord(payload) || !Array.isArray(payload.items)) {
        return { timeline }
      }
      const items: AgentTodoItem[] = payload.items.filter(isRecord).map((item, index) => ({
        id: asString(item.id, `todo-${index}`),
        content: asString(item.content, `步骤 ${index + 1}`),
        status: mapTodoStatus(item.status),
      }))
      const allDone = items.length > 0 && items.every((item) => item.status === 'completed')
      const current = items.find((item) => item.status === 'in_progress')
      return {
        timeline: upsertBlock(timeline, {
          id: 'todos',
          type: 'todos',
          items,
          forceOpen: !allDone,
          collapsedByDefault: allDone,
        }),
        activity: current
          ? { label: current.content, kind: 'planning' }
          : allDone
            ? { label: '计划步骤已完成', kind: 'done' }
            : { label: '正在更新执行计划…', kind: 'planning' },
      }
    }

    case 'answer': {
      if (!isRecord(payload)) return { timeline }
      const id = asString(payload.id, `answer-${Date.now()}`)
      const text = asString(payload.text)
      return {
        // 正文开始输出 ⇒ 当前轮思维链必然已结束，收尾残留的「思考中」块
        timeline: closeRunningReasoning(upsertBlock(timeline, { id, type: 'answer', text })),
        // 不带 kind：正文输出不是「思考」，避免活动条出现「思考中 正在输出正文…」的错位提示
        activity: { label: '正在输出正文…' },
        // 必须带 running：否则 patchMessage 的 spread 会把 status 覆盖为 undefined，
        // AgentRunCard 的 running 变 false 导致正文打字机被禁用（整段跳变）
        status: 'running',
      }
    }

    case 'process': {
      if (!isRecord(payload)) return { timeline }
      const id = asString(payload.id, `process-${Date.now()}`)
      const status = asString(payload.status, 'running')
      const awaiting = status === 'awaiting' || status === 'awaiting_confirmation'
      const summary = asString(payload.summary, '处理中')
      // 成功的工具调用不在时间线展示（无可见内容，徒占一行），仅在活动条提示进度
      if (status === 'done' && summary === '工具调用') {
        return {
          timeline,
          activity: { label: '工具调用完成', kind: 'tool' },
        }
      }
      return {
        timeline: upsertBlock(timeline, {
          id,
          type: 'process',
          summary,
          status,
          collapsedByDefault: !awaiting,
          forceOpen: awaiting,
          lines: mapProcessLines(payload.lines),
        }),
        activity: awaiting
          ? { label: summary, kind: 'confirm' }
          : status === 'failed'
            ? { label: summary, kind: 'done' }
            : { label: summary, kind: 'tool' },
      }
    }

    case 'subagent': {
      if (!isRecord(payload)) return { timeline }
      const id = asString(payload.id, `subagent-${Date.now()}`)
      const status = asString(payload.status, 'running')
      const title = asString(payload.title || payload.summary, '子智能体任务')
      const activity = parseActivity(payload.activity) ?? {
        label: asString(payload.activityLabel, status === 'running' ? '子智能体执行中…' : '子智能体已结束'),
        kind: status === 'running' ? 'subagent' : 'done',
      }
      return {
        timeline: upsertBlock(timeline, {
          id,
          type: 'subagent',
          title,
          status,
          summary: typeof payload.summary === 'string' ? payload.summary : undefined,
          collapsedByDefault: status !== 'running',
          forceOpen: status === 'running',
          lines: mapProcessLines(payload.lines),
          activity,
        }),
        activity: {
          label: `等待子智能体：${title}`,
          kind: 'subagent',
        },
        status: status === 'running' ? 'running' : undefined,
      }
    }

    case 'confirmation_required': {
      const pendingAction = parsePendingAction(payload)
      // ask_user 走同一确认通道：过程块/活动文案统一「提问中」语义，回答后由后端改为「已回答提问/未回答提问」
      const isAskUser = pendingAction?.toolName === 'ask_user'
      const awaitLabel = isAskUser
        ? '提问中'
        : pendingAction
          ? `等待确认「${agentToolLabel(pendingAction.toolName)}」`
          : '等待你确认下一步动作'
      const result: ApplyAgentStreamEventResult = {
        timeline,
        pendingAction,
        status: 'awaiting_confirmation',
        showConfirm: Boolean(pendingAction),
        runPublicId: pendingAction?.runPublicId ?? null,
        activity: {
          label: awaitLabel,
          kind: 'confirm',
        },
      }
      if (pendingAction) {
        // ask_user 无引导语时不发 note 行（标题「提问中」已足够），否则会渲染出只剩状态点的空行
        const noteDetail = pendingAction.assistantThought?.trim() ||
          (isAskUser ? '' : `准备执行「${agentToolLabel(pendingAction.toolName)}」`)
        // 时间线生图：确认前先把生成参数亮出来（模型/画幅/张数/参考图/提示词），用户确认的就是这批参数
        const paramLines = isAskUser ? [] : generationParamLines(pendingAction.input, pendingAction.token)
        const baseLines: AgentProcessLine[] = noteDetail
          ? [
              {
                id: `confirm-${pendingAction.token}-note`,
                kind: 'note',
                // 不放标题行：有引导语时直接展示
                label: '',
                detail: noteDetail,
                status: 'awaiting_confirmation',
              },
            ]
          : []
        result.timeline = upsertBlock(timeline, {
          id: `confirm-${pendingAction.token}`,
          type: 'process',
          summary: awaitLabel,
          status: 'awaiting_confirmation',
          collapsedByDefault: false,
          forceOpen: true,
          lines: [...baseLines, ...paramLines],
        })
      }
      return result
    }

    case 'final': {
      const response = parseFinalResponse(payload)
      if (!response) return { timeline }
      const pendingAction = response.pendingAction
        ? parsePendingAction(response.pendingAction)
        : null
      const status = asString(response.status, 'succeeded')
      return {
        // 运行终态：任何残留的「思考中」块就地收尾，避免运行结束后状态仍停在思考
        timeline: closeRunningReasoning(timeline),
        pendingAction,
        status,
        showConfirm: status === 'awaiting_confirmation' && Boolean(pendingAction),
        runPublicId: typeof response.runPublicId === 'string' ? response.runPublicId : null,
        activity:
          status === 'awaiting_confirmation'
            ? { label: '等待你确认下一步动作', kind: 'confirm' }
              : status === 'failed'
                ? { label: '运行失败', kind: 'error' }
                : status === 'cancelled'
                  ? { label: '已取消', kind: 'error' }
                  : { label: '已完成', kind: 'done' },
      }
    }

    case 'error': {
      if (!isRecord(payload)) {
        return {
          timeline: upsertBlock(timeline, {
            id: `error-${Date.now()}`,
            type: 'answer',
            text: '智能体运行失败。',
          }),
          status: 'failed',
          showConfirm: false,
          activity: { label: '运行失败', kind: 'error' },
        }
      }
      const errorMessage = asString(payload.errorMessage, '智能体运行失败。')
      // 时间线生图失败：优先转成失败卡片（带平台与重新生成按钮），错误详情保留在正文
      if (asString(payload.toolName) === 'timeline.generate') {
        const taskIdMatch = /taskId=([A-Za-z0-9-]+)/.exec(errorMessage)
        const platformMatch = /平台=([^）:：]+)/.exec(errorMessage)
        if (taskIdMatch) {
          return {
            timeline: upsertBlock(timeline, {
              id: `gen-${taskIdMatch[1]}`,
              type: 'generation-card',
              data: {
                taskId: taskIdMatch[1],
                status: 'failed',
                platform: platformMatch?.[1]?.trim() || undefined,
                errorMessage: errorMessage.replace(/^生成失败（[^）]*）：/, ''),
              },
            }),
            status: 'failed',
            showConfirm: false,
            runPublicId: typeof payload.runPublicId === 'string' ? payload.runPublicId : undefined,
            activity: { label: '生图任务失败', kind: 'error' },
          }
        }
      }
      return {
        timeline: upsertBlock(timeline, {
          id: asString(payload.id, `error-${Date.now()}`),
          type: 'answer',
          text: errorMessage,
        }),
        status: 'failed',
        showConfirm: false,
        runPublicId: typeof payload.runPublicId === 'string' ? payload.runPublicId : undefined,
        // 状态行只展示状态本身（红点 + "运行失败"）；错误详情只出现在下方 answer 块，避免重复
        activity: { label: '运行失败', kind: 'error' },
      }
    }

    case 'reasoning': {
      if (!isRecord(payload)) return { timeline }
      const text = asString(payload.text)
      const round = Number(payload.round ?? 0)
      const done = payload.done === true
      // done 且空文本无信息量，忽略；非 done 空文本是「回合开始」占位事件，需立即建块
      if (!text.trim() && done) return { timeline }
      const roundKey = Number.isFinite(round) && round > 0 ? round : 0
      // 以轮次为幂等键：同一轮的多次节流推送只覆盖不追加
      const id = `reasoning-line-${roundKey || `x${Date.now()}`}`
      const line: AgentProcessLine = {
        id,
        kind: 'thought',
        label: '',
        detail: text,
        status: done ? 'done' : 'running',
      }
      // 轮次是顺序推进的：新回合事件到达时，旧回合仍卡在 running 的思考块直接收尾
      //（该轮可能整轮没有思维链输出，例如纯工具调用轮）
      const summarize = (lines: AgentProcessLine[]): string => {
        const rounds = lines
          .map((item) => Number(item.id.replace('reasoning-line-', '')))
          .filter((value) => Number.isFinite(value) && value > 0)
        const first = rounds[0]
        const last = rounds[rounds.length - 1] ?? first
        if (!last) return done ? '思考完成' : '思考中'
        if (done && rounds.length > 1) return `第 ${first}~${last} 轮思考完成`
        return `第 ${last} 轮${done ? '思考完成' : '思考中'}`
      }
      // 幂等优先：先按轮次找已有块（不看位置）。否则一轮的 done 事件会因「最后一个块
      // 是正文」而 upsert 出重复的幽灵块，旧块则永久卡在「思考中」
      const ownerIndex = timeline.findIndex(
        (item) =>
          item.type === 'process' &&
          item.mergeKey === 'reasoning' &&
          item.lines.some((item2) => item2.id === line.id),
      )
      if (ownerIndex >= 0) {
        const target = timeline[ownerIndex]
        if (target.type !== 'process') return { timeline }
        const lines = target.lines.map((item) => (item.id === line.id ? line : item))
        const allDone = done && lines.every((item) => item.status === 'done')
        const merged: AgentTimelineBlock = {
          ...target,
          count: lines.length,
          status: allDone ? 'done' : 'running',
          forceOpen: !allDone,
          summary: allDone ? withElapsedSeconds(summarize(lines), target.startedAt) : summarize(lines),
          lines,
        }
        const next = timeline.slice()
        next[ownerIndex] = merged
        const tail = text.replace(/\s+/g, ' ').trim().slice(-80)
        return {
          timeline: closeRunningReasoning(next, id),
          status: 'running',
          activity: allDone ? undefined : { label: tail || `${merged.summary}…`, kind: 'thinking' as const },
        }
      }
      // 连续到达的思考（中间没有正文/其它块）并入同一折叠块；新轮次追加一行
      const last = timeline[timeline.length - 1]
      if (last && last.type === 'process' && last.mergeKey === 'reasoning') {
        const lines = [...last.lines, line]
        const merged: AgentTimelineBlock = {
          ...last,
          count: lines.length,
          status: 'running',
          forceOpen: true,
          summary: summarize(lines),
          lines,
        }
        const next = timeline.slice(0, -1)
        next.push(merged)
        const tail = text.replace(/\s+/g, ' ').trim().slice(-80)
        return {
          timeline: closeRunningReasoning(next, id),
          status: 'running',
          activity: { label: tail || `${merged.summary}…`, kind: 'thinking' as const },
        }
      }
      const blockId = `reasoning-${roundKey || Date.now()}`
      return {
        timeline: closeRunningReasoning(upsertBlock(timeline, {
          id: blockId,
          type: 'process',
          summary: summarize([line]),
          status: done ? 'done' : 'running',
          // 计时起点：思考中由折叠块标题实时跳动（用时 n 秒），收尾时烙进标题
          startedAt: done ? undefined : Date.now(),
          // 推理是用户主动想看的过程，默认展开；结束后可自行收起
          collapsedByDefault: false,
          forceOpen: !done,
          count: 1,
          mergeKey: 'reasoning',
          lines: [line],
        }), id),
        status: 'running',
        activity: done
          ? undefined
          : { label: text.replace(/\s+/g, ' ').trim().slice(-80) || '思考中…', kind: 'thinking' as const },
      }
    }

    case 'tool_thought': {
      if (!isRecord(payload)) return { timeline }
      return {
        timeline,
        activity: {
          label: asString(payload.content, '正在思考…').slice(0, 80) || '正在思考…',
          kind: 'thinking',
        },
        status: 'running',
      }
    }

    case 'generation_enqueued': {
      // 时间线生图任务入队即推卡片：等待生成期间就展示任务号 + 模型/画幅/张数 + 骨架，
      // 终态由 tool_result / task_updated 推送按同 taskId upsert 覆盖。
      // phase=waiting 是后端同步等待期间每 10 秒的「监听中」心跳，只刷活动条不建块
      if (!isRecord(payload)) return { timeline }
      const taskId = asString(payload.taskId)
      if (!taskId) return { timeline }
      if (payload.phase === 'waiting') {
        const waited = Number(payload.waitedSeconds ?? 0)
        return {
          timeline,
          activity: {
            label: `正在监听生成任务（已等待 ${Number.isFinite(waited) && waited > 0 ? Math.round(waited) : 0} 秒）`,
            kind: 'waiting',
          },
        }
      }
      return {
        timeline: upsertBlock(timeline, {
          id: `gen-${taskId}`,
          type: 'generation-card',
          data: {
            taskId,
            status: 'running',
            model: asString(payload.model) || undefined,
            modelLabel: asString(payload.modelLabel) || undefined,
            aspect: asString(payload.aspect) || undefined,
            n: typeof payload.n === 'number' ? payload.n : undefined,
            platform: asString(payload.platform) || undefined,
            prompt: asString(payload.prompt) || undefined,
            startedAt: Date.now(),
          },
        }),
        status: 'running',
        activity: { label: '生图任务已提交，正在监听生成进度', kind: 'waiting' },
      }
    }

    case 'tool_result': {
      // 工具返回图库类数据时生成图片块：生图历史 / 参考图等直接在时间线渲染
      if (!isRecord(payload)) return { timeline }
      const toolName = asString(payload.toolName)
      // 时间线生图：渲染为生图任务卡片（基本信息 + 状态 + 失败重试），而不是普通图片条
      if (toolName === 'timeline.generate') {
        const output = isRecord(payload.output) ? payload.output : null
        const taskId = output ? asString(output.taskId) : ''
        const status = output ? asString(output.status) : ''
        if (!taskId || status === 'select_model' || !output) return { timeline }
        if (status === 'queued' || status === 'processing' || status === 'completed') {
          // 沿用入队卡片已记录的计时起点：完成时换算成总用时烙进卡片
          const existing = timeline.find(
            (item) => item.type === 'generation-card' && item.id === `gen-${taskId}`,
          )
          const prevData = existing && existing.type === 'generation-card' ? existing.data : undefined
          const elapsedSeconds =
            status === 'completed' && prevData?.startedAt != null
              ? Math.max(1, Math.round((Date.now() - prevData.startedAt) / 1000))
              : prevData?.elapsedSeconds
          return {
            timeline: upsertBlock(timeline, {
              id: `gen-${taskId}`,
              type: 'generation-card',
              data: {
                taskId,
                status: status === 'completed' ? 'completed' : 'running',
                model: asString(output.model) || prevData?.model || undefined,
                modelLabel: asString(output.modelLabel) || prevData?.modelLabel || undefined,
                aspect: asString(output.aspect) || prevData?.aspect || undefined,
                n: typeof output.n === 'number' ? output.n : prevData?.n,
                platform: asString(output.platform) || prevData?.platform || undefined,
                prompt: prevData?.prompt,
                startedAt: prevData?.startedAt,
                elapsedSeconds,
                images: Array.isArray(output.images)
                  ? output.images.filter((src): src is string => typeof src === 'string' && src.trim().length > 0)
                  : undefined,
              },
            }),
            activity:
              status === 'completed'
                ? { label: '生图任务完成', kind: 'done' }
                : { label: '生图任务进行中，正在监听生成进度', kind: 'waiting' },
          }
        }
        return { timeline }
      }
      const images = extractImagesFromToolOutput(toolName, payload.output)
      if (!images.length) return { timeline }
      // id 取首图 URL 尾段：同一次工具调用的重复事件幂等合并，不同分页自然分块
      const id = `images-${toolName}-${images[0].src.replace(/[^a-zA-Z0-9]/g, '').slice(-32)}`
      return {
        timeline: appendImagesBlock(timeline, id, toolName, images),
        activity: { label: '已获取图片', kind: 'tool' },
      }
    }

    case 'task': {
      if (!isRecord(payload)) return { timeline }
      return {
        timeline,
        activity: {
          label: `下一步：${asString(payload.title, '执行计划步骤')}`,
          kind: 'planning',
        },
        status: 'running',
      }
    }

    case 'plan': {
      return {
        timeline,
        activity: { label: '正在生成执行计划…', kind: 'planning' },
        status: 'running',
      }
    }

    default:
      return { timeline }
  }
}
